import classNames from 'classnames';
import {ReactElement, useCallback, useMemo, useRef, FocusEvent} from 'react';
import {IMaskInput} from 'react-imask';

const dayMonthRegex = /^[0-9]{0,2}$/;
const yearRegex = /^[0-9]{0,4}$/;

interface DateComponents {
  day: string;
  month: string;
  year: string;
}

function getDateComponents(date: string): DateComponents {
  const components = date?.split('-') ?? [];

  return {
    day: (components.length > 0 ? components[0] : '') ?? '',
    month: (components.length > 1 ? components[1] : '') ?? '',
    year: (components.length > 2 ? components[2] : '') ?? '',
  };
}

function getPadded(value: string) {
  const parsedValue = parseInt(value);
  return !isNaN(parsedValue) && 0 < parsedValue
    ? value.padStart(2, '0')
    : value;
}

interface DateOfBirthFieldProps {
  value: string;
  onChange: (formattedDateStr: string) => void;
  labelClassName?: string;
  titleClassName?: string;
  label?: string;
  error: string;
  required?: boolean;
}
export default function DateOfBirthField({
  value,
  onChange,
  labelClassName,
  titleClassName,
  label,
  error,
  required = false,
}: DateOfBirthFieldProps): ReactElement {
  const date = useMemo(() => getDateComponents(value), [value]);

  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const setValue = useCallback(
    (name, value) => {
      const getComp = (comp: keyof DateComponents) =>
        (comp === name ? value : date[comp]) ?? '';

      onChange(`${getComp('day')}-${getComp('month')}-${getComp('year')}`);
    },
    [date, onChange],
  );

  const changeHandler = useCallback(
    (name, value) => {
      const parsedValue = parseInt(value);
      switch (name) {
        case 'day':
          if (
            !isNaN(parsedValue) &&
            (value.length == 2 ||
              // if the number is between 3 and 10
              // then it cannot be 1_, 2_, or 30, 31
              (3 < parsedValue && parsedValue < 10))
          ) {
            value = getPadded(value);
            if (dayRef?.current && document.activeElement === dayRef?.current) {
              monthRef.current?.focus();
            }
          }
          break;
        case 'month':
          if (
            !isNaN(parsedValue) &&
            (value.length == 2 ||
              // if the number is between 3 and 10
              // then it cannot be 10, 11, or 12
              (1 < parsedValue && parsedValue < 10))
          ) {
            value = getPadded(value);
            if (
              monthRef?.current &&
              document.activeElement === monthRef?.current
            ) {
              yearRef.current?.focus();
            }
          }
          break;
      }
      setValue(name, value);
    },
    [setValue],
  );

  const blurHandler = useCallback(
    (event: FocusEvent<HTMLInputElement>) =>
      setValue(
        event.currentTarget.name,
        getPadded(date[event.currentTarget.name]),
      ),
    [date, setValue],
  );

  const acceptDayHandler = useCallback(
    (value) => changeHandler('day', value),
    [changeHandler],
  );
  const acceptMonthHandler = useCallback(
    (value) => changeHandler('month', value),
    [changeHandler],
  );
  const acceptYearHandler = useCallback(
    (value) => changeHandler('year', value),
    [changeHandler],
  );

  return (
    <label className={classNames('dateCustom', labelClassName)}>
      {label && (
        <div className={classNames('dateCustom__title', titleClassName)}>
          <span>{label}</span>
        </div>
      )}
      <div className="dateCustom__inputs">
        <IMaskInput
          inputRef={(el) => (dayRef.current = el as HTMLInputElement)}
          mask={dayMonthRegex}
          value={date.day}
          onAccept={acceptDayHandler}
          onBlur={blurHandler}
          name="day"
          placeholder="DD"
          inputMode="numeric"
          autoComplete="off"
          required={required}
          className={classNames('form__element', {
            'form__element-error': error,
          })}
        />
        <IMaskInput
          inputRef={(el) => (monthRef.current = el as HTMLInputElement)}
          mask={dayMonthRegex}
          value={date.month}
          onAccept={acceptMonthHandler}
          onBlur={blurHandler}
          name="month"
          placeholder="MM"
          inputMode="numeric"
          autoComplete="off"
          required={required}
          className={classNames('form__element', {
            'form__element-error': error,
          })}
        />
        <IMaskInput
          inputRef={(el) => (yearRef.current = el as HTMLInputElement)}
          mask={yearRegex}
          value={date.year}
          onAccept={acceptYearHandler}
          name="year"
          placeholder="YYYY"
          inputMode="numeric"
          autoComplete="off"
          required={required}
          className={classNames('form__element', {
            'form__element-error': error,
          })}
        />
      </div>

      {error && <p className="error-msg">{error}</p>}
    </label>
  );
}
